import React, { useState, useEffect, useMemo, useRef } from 'react';
import "ag-grid-community/styles/ag-grid.css"; // Core CSS
import "ag-grid-community/styles/ag-theme-quartz.css"; // Theme
import "./LeaderboardAgGrid.css"; // 确保导入自定义样式
import "./LeaderboardComp.css"; // 确保导入自定义样式
import { AgGridReact } from 'ag-grid-react';
import { ColDef, GridReadyEvent, GridSizeChangedEvent } from 'ag-grid-community';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Slider from '@mui/material/Slider';
import { getLeaderboard, getColumnDefs } from './leaderboardLib';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Paper from '@mui/material/Paper';
import Divider from '@mui/material/Divider';
import Card from '@mui/material/Card';
import CardMedia from '@mui/material/CardMedia';
import CardContent from '@mui/material/CardContent';
import { styled } from '@mui/material/styles';

// 定义Case接口
interface Case {
  name: string;
  display_name?: string;
  complexity_type: string;
  complexity_values: number[];
}

// 定义图片接口
interface ImageSet {
  inputSrc: string;
  outputSrc: string;
  index: number;
}

const FONT_FAMILY = "'JetBrains Mono', monospace";

// 修改图片卡片样式 - 更紧凑的单行布局
const ImageCard = styled(Card)(({ theme }) => ({
  width: 200,
  height: 'auto',
  margin: theme.spacing(0.5),
  display: 'flex',
  flexDirection: 'column',
}));

// 修改图片容器样式 - 单行布局
const ImageContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  alignItems: 'flex-start',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  flexWrap: 'nowrap',
  overflow: 'auto',
}));

// 增大散点图卡片样式
const ScatterPlotCard = styled(Card)(({ theme }) => ({
  width: '100%',
  maxWidth: 700, // 进一步增大最大宽度
  height: 'auto',
  boxShadow: theme.shadows[4],
}));

// 缩短按钮样式 - 减少宽度
const ShortenedButton = styled(Button)(({ theme }) => ({
  fontSize: '0.8rem',
  padding: theme.spacing(0.7, 1.2),
  minWidth: 'auto',
  height: 36,
  width: '80%', // 缩短按钮宽度到80%
  marginBottom: theme.spacing(0.8),
  textTransform: 'none',
  borderRadius: theme.spacing(0.5),
}));

const Leaderboard = React.memo(function LeaderboardComponent(props: any) {
  let args = props.args;
  const { 
    performances = [], 
    models = [], 
    level = [],
    cases = [] // 添加cases数据
  } = args || {};
  
  // 添加modelsDict定义
  const modelsDict = useMemo(() => {
    return models.reduce((dict: Record<string, string>, model: any) => {
      dict[model.model_name] = model.model_name;
      return dict;
    }, {});
  }, [models]);
  
  // 提取所有不同的task（来自display_name）
  const tasks = useMemo(() => {
    const taskSet = new Set<string>();
    cases.forEach((caseItem: Case) => {
      if (caseItem.display_name) {
        taskSet.add(caseItem.display_name);
      }
    });
    return Array.from(taskSet);
  }, [cases]);

  // 添加状态变量
  const [selectedTask, setSelectedTask] = useState<string>("");
  const [selectedCase, setSelectedCase] = useState<string>("");
  const [selectedComplexity, setSelectedComplexity] = useState<number | null>(null);
  const [images, setImages] = useState<ImageSet[]>([]);
  const [loadingImages, setLoadingImages] = useState<boolean>(false);
  const [imageError, setImageError] = useState<string | null>(null);
  const [scatterPlotSrc, setScatterPlotSrc] = useState<string>("");
  const [scatterPlotError, setScatterPlotError] = useState<string | null>(null);

  // 在 useEffect 中正确初始化 selectedTask
  useEffect(() => {
    if (tasks.length > 0 && !tasks.includes(selectedTask)) {
      setSelectedTask(tasks[0]);
    }
  }, [tasks, selectedTask]);
  
  // 根据选中的task过滤cases
  const filteredCases = useMemo(() => {
    return cases.filter((caseItem: Case) => 
      caseItem.display_name === selectedTask
    );
  }, [cases, selectedTask]);

  // 在任务变更时，选择该任务下的第一个案例
  useEffect(() => {
    if (filteredCases.length > 0) {
      setSelectedCase(filteredCases[0].name);
    } else {
      setSelectedCase("");
      setSelectedComplexity(null);
    }
  }, [selectedTask, filteredCases]);
  
  // 获取当前选中case的复杂度信息
  const currentCase = useMemo(() => {
    return cases.find((c: Case) => c.name === selectedCase) || null;
  }, [cases, selectedCase]);
  
  // 复杂度滑块标记
  const complexityMarks = useMemo(() => {
    if (!currentCase || !currentCase.complexity_values || !Array.isArray(currentCase.complexity_values)) return [];
    
    return currentCase.complexity_values.map((value: number) => ({
      value,
      label: `${value}`
    }));
  }, [currentCase]);
  
  // 在组件挂载和case变更时初始化复杂度
  useEffect(() => {
    if (currentCase && currentCase.complexity_values && currentCase.complexity_values.length > 0) {
      setSelectedComplexity(currentCase.complexity_values[0]);
    } else {
      setSelectedComplexity(null);
    }
  }, [currentCase]);

  // 任务选择处理函数
  const handleTaskChange = (taskName: string) => {
    setSelectedTask(taskName);
  };

  // 案例选择处理函数
  const handleCaseChange = (caseName: string) => {
    setSelectedCase(caseName);
    const newCase = cases.find((c: Case) => c.name === caseName);
    
    if (newCase && newCase.complexity_values && newCase.complexity_values.length > 0) {
      const newComplexity = newCase.complexity_values[0];
      setSelectedComplexity(newComplexity);
      updateGridData(caseName, newComplexity);
    } else {
      setSelectedComplexity(null);
      updateGridData(caseName, null);
    }
  };
  
  // 添加复杂度滑块处理函数
  const complexitySliderHandleChange = (event: Event, newValue: number | number[]) => {
    const newComplexity = Array.isArray(newValue) ? newValue[0] : newValue;
    setSelectedComplexity(newComplexity);
    updateGridData(selectedCase, newComplexity);
  };
  
  // 更新表格数据的函数
  const updateGridData = (caseName: string, complexityValue: number | null) => {
    const newDf = getLeaderboard(
      performances,
      models,
      undefined,
      caseName,
      complexityValue
    );
    
    if (gridRef.current && gridRef.current.api) {
      setRowData(newDf);
    }
  };

  // 修改图片加载函数
  const loadImages = React.useCallback(async () => {
    if (!selectedTask || !selectedCase || selectedComplexity === null) {
      setImages([]);
      setImageError("Please select a task, case, and complexity to view images.");
      return;
    }
    
    setLoadingImages(true);
    setImageError(null);
    
    try {
      const basePath = `./all_images/${encodeURIComponent(selectedTask)}/${selectedCase}`;
      const folderName = `${selectedComplexity}_test`;
      const fullPath = `${basePath}/${folderName}`;
      
      console.log("Attempting to load images from:", fullPath);
      
      const maxPairs = 2;
      const imageSetArray: ImageSet[] = [];
      
      for (let i = 1; i <= maxPairs; i++) {
        try {
          const inputSrc = `${fullPath}/${i}_input.png`;
          const outputSrc = `${fullPath}/${i}_output.png`;
          
          const inputExists = await checkImageExists(inputSrc);
          const outputExists = await checkImageExists(outputSrc);
          
          if (inputExists && outputExists) {
            imageSetArray.push({ inputSrc, outputSrc, index: i });
          }
        } catch (error) {
          console.warn(`Failed to load image pair ${i}:`, error);
        }
      }
      
      if (imageSetArray.length === 0) {
        const debugInfo = `Task: ${selectedTask}, Case: ${selectedCase}, Complexity: ${selectedComplexity}, Path: ${fullPath}`;
        console.log("No images found. Debug info:", debugInfo);
      } else {
        setImages(imageSetArray);
      }
    } catch (error) {
      console.error("Error loading images:", error);
      setImageError("Failed to load images. Please try again.");
    } finally {
      setLoadingImages(false);
    }
  }, [selectedTask, selectedCase, selectedComplexity]);

  // 新增：加载散点图的函数
  const loadScatterPlot = React.useCallback(async () => {
    if (!selectedCase) {
      setScatterPlotSrc("");
      setScatterPlotError("Please select a case to view scatter plot.");
      return;
    }

    setScatterPlotError(null);
    
    try {
      const scatterPath = `./images_scatter/case_${selectedCase}.png`;
      console.log("Attempting to load scatter plot from:", scatterPath);
      
      const scatterExists = await checkImageExists(scatterPath);
      
      if (scatterExists) {
        setScatterPlotSrc(scatterPath);
      } else {
        setScatterPlotSrc("");
        console.log("No scatter plot found for case:", selectedCase);
      }
    } catch (error) {
      console.error("Error loading scatter plot:", error);
      setScatterPlotError("Failed to load scatter plot.");
    }
  }, [selectedCase]);

  // 改进图片检测方法
  const checkImageExists = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      
      const timeout = setTimeout(() => {
        console.warn(`Image load timeout: ${url}`);
        resolve(false);
      }, 5000);
      
      img.onload = () => {
        clearTimeout(timeout);
        console.log(`Image loaded successfully: ${url}`);
        resolve(true);
      };
      
      img.onerror = () => {
        clearTimeout(timeout);
        console.warn(`Image load failed: ${url}`);
        resolve(false);
      };
      
      img.src = `${url}?t=${new Date().getTime()}`;
    });
  };
  
  // 当任务、案例或复杂度变更时，加载图片
  useEffect(() => {
    loadImages();
  }, [loadImages]);

  // 当案例变更时，加载散点图
  useEffect(() => {
    loadScatterPlot();
  }, [loadScatterPlot]);
  
  // 使用更新后的getLeaderboard获取数据
  const leaderboard = useMemo(() => {
    return getLeaderboard(
      performances,
      models,
      undefined,
      selectedCase,
      selectedComplexity
    );
  }, [performances, models, selectedCase, selectedComplexity]);
  
  // 计算有多少不同模型
  const numModels = performances && Array.isArray(performances) 
    ? new Set(performances.filter(p => p.model).map(p => p.model)).size 
    : 0;

  // 获取当前level值
  const currentLevel = performances && performances.length > 0 ? performances[0].level : 0;

  const columnNames = useMemo(() => {
    return leaderboard.length > 0 ? Object.keys(leaderboard[0]) : [];
  }, [leaderboard]);

  const defaultColDef = useMemo(() => {
    return {
      flex: 1,
    }
  }, [])

  const rowClassRules = useMemo(() => {
    return {}
  }, [])

  const gridRef = useRef<AgGridReact>(null);
  const [rowData, setRowData] = useState(leaderboard)

  useEffect(() => {
    setRowData(leaderboard);
  }, [leaderboard]);

  const [columnDefs, setColumnDefs] = useState<ColDef[]>(
    getColumnDefs(columnNames, modelsDict)
  );

  useEffect(() => {
    setColumnDefs(getColumnDefs(columnNames, modelsDict));
  }, [columnNames, modelsDict]);

  // ********* Styles and return *********

  const muiTheme = createTheme({
    palette: {
      mode: props.theme.base,
    },
    typography: {
      fontFamily: FONT_FAMILY,
    },
  })

  const agGridTheme =
    props.theme.base === "dark" ? "ag-theme-quartz-dark" : "ag-theme-quartz"

  // 修改表格样式 - 增加宽度
  const gridStyle = useMemo(
    () => ({
      height: `${Math.min(42 * (rowData.length || 10), 1000)}px`,
      width: '100%',
      minWidth: '800px',
      maxWidth: '1200px',
      "--ag-font-family": FONT_FAMILY,
      overflow: "auto",
      margin: "auto",
    }),
    [rowData]
  ) as React.CSSProperties;

  const autoSizeStrategy = {
    type: 'fitCellContents'
  } as const;

  // 更新返回的JSX - 左侧垂直排列，右侧大散点图
  return (
    <div style={{ width: "100%", height: "100%" }}>
      <ThemeProvider theme={muiTheme}>
        <CssBaseline />
        
        {/* 主要内容区域 */}
        <Box sx={{ width: "100%", maxWidth: "1400px", margin: "0 auto", padding: 2 }}>
          
          {/* 上半部分：左侧选择区域 + 右侧散点图 */}
          <Grid container spacing={4}>
            {/* 左侧：任务和案例选择 - 垂直排列 */}
            <Grid item xs={12} md={4}>
              {/* 任务选择 */}
              <Box sx={{ marginBottom: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Task Selection:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  {tasks.map((taskName) => (
                    <ShortenedButton
                      key={taskName}
                      variant={selectedTask === taskName ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => handleTaskChange(taskName)}
                    >
                      {taskName}
                    </ShortenedButton>
                  ))}
                </Box>
              </Box>

              {/* 案例选择 - 在任务下方 */}
              <Box>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Case Selection:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  {filteredCases.map((caseItem: Case) => (
                    <ShortenedButton
                      key={caseItem.name}
                      variant={selectedCase === caseItem.name ? "contained" : "outlined"}
                      color="primary"
                      onClick={() => handleCaseChange(caseItem.name)}
                    >
                      {caseItem.name}
                    </ShortenedButton>
                  ))}
                </Box>
              </Box>
            </Grid>

            {/* 右侧：散点图 - 占据更大空间 */}
            <Grid item xs={12} md={8}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', height: '100%' }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                  Case Scatter Plot:
                </Typography>
                {scatterPlotSrc ? (
                  <ScatterPlotCard>
                    <CardMedia
                      component="img"
                      height="450" // 进一步增加高度
                      image={scatterPlotSrc}
                      alt={`Scatter plot for case ${selectedCase}`}
                      sx={{ objectFit: 'contain' }}
                    />
                    <CardContent sx={{ padding: 1 }}>
                      <Typography variant="body2" color="text.secondary" align="center">
                        Case: {selectedCase}
                      </Typography>
                    </CardContent>
                  </ScatterPlotCard>
                ) : (
                  <Box sx={{ 
                    width: '100%', 
                    height: 450, 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    border: '2px dashed #ccc',
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      {scatterPlotError || "No scatter plot available"}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* 复杂度滑块 */}
          {currentCase && currentCase.complexity_values && currentCase.complexity_values.length > 0 && (
            <Box sx={{ width: "100%", marginTop: 3, marginBottom: 3 }}>
              <Typography id="complexity-slider-label" gutterBottom sx={{ fontWeight: 'bold' }}>
                {`Complexity (${currentCase.complexity_type}):`}
              </Typography>
              <Slider
                value={selectedComplexity || 0}
                onChange={complexitySliderHandleChange}
                step={null}
                marks={complexityMarks}
                min={complexityMarks[0]?.value || 0}
                max={complexityMarks[complexityMarks.length - 1]?.value || 0}
                valueLabelDisplay="auto"
                track={false}
                disabled={!selectedComplexity}
              />
            </Box>
          )}

          {/* 表格区域 */}
          <Box sx={{ width: "100%", display: "flex", justifyContent: "center", marginBottom: 3 }}>
            <div style={gridStyle} className={agGridTheme}>
              <AgGridReact
                ref={gridRef}
                rowData={rowData}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                rowClassRules={rowClassRules}
                rowSelection={"multiple"}
                enableCellTextSelection={true}
                tooltipShowDelay={0}
                autoSizeStrategy={autoSizeStrategy}
                onGridReady={(params) => {
                  params.api.sizeColumnsToFit()
                  params.api.autoSizeAllColumns(false)
                  params.api.resetRowHeights()
                }}
                onGridSizeChanged={(params: GridSizeChangedEvent) => {
                  params.api.sizeColumnsToFit()
                  params.api.autoSizeAllColumns(false)
                  params.api.resetRowHeights()
                }}
                onGridColumnsChanged={(params: any) => {
                  params.api.sizeColumnsToFit()
                  params.api.autoSizeAllColumns(false)
                  params.api.resetRowHeights()
                }}
                onRowDataUpdated={(params: any) => {
                  params.api.sizeColumnsToFit()
                  params.api.autoSizeAllColumns(false)
                  params.api.resetRowHeights()
                }}
              />
            </div>
          </Box>
          
          {/* 图片显示区域 - 单行四张图片 */}
          <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom align="center" sx={{ fontWeight: 'bold' }}>
              Example Images
            </Typography>
            
            {loadingImages && (
              <Typography align="center">Loading images...</Typography>
            )}
            
            {!loadingImages && imageError && (
              <Typography align="center" color="text.secondary" variant="body2">
                {imageError}
              </Typography>
            )}
            
            {!loadingImages && !imageError && images.length > 0 && (
              <ImageContainer>
                {images.map((imageSet) => (
                  <React.Fragment key={imageSet.index}>
                    <ImageCard>
                      <CardContent sx={{ padding: 1 }}>
                        <Typography variant="caption" color="text.secondary" align="center">
                          Example {imageSet.index} - Input
                        </Typography>
                      </CardContent>
                      <CardMedia
                        component="img"
                        height="150"
                        image={imageSet.inputSrc}
                        alt={`Input image ${imageSet.index}`}
                        sx={{ objectFit: 'contain' }}
                      />
                    </ImageCard>
                    
                    <ImageCard>
                      <CardContent sx={{ padding: 1 }}>
                        <Typography variant="caption" color="text.secondary" align="center">
                          Example {imageSet.index} - Output
                        </Typography>
                      </CardContent>
                      <CardMedia
                        component="img"
                        height="150"
                        image={imageSet.outputSrc}
                        alt={`Output image ${imageSet.index}`}
                        sx={{ objectFit: 'contain' }}
                      />
                    </ImageCard>
                  </React.Fragment>
                ))}
              </ImageContainer>
            )}
          </Box>
        </Box>
      </ThemeProvider>
    </div>
  );
});

export default Leaderboard;